import { getValidAccessToken } from "./authToken";

/** A fetched file, plus the two things the server says about it. */
export type FetchedFile = {
  blob: Blob;
  /** From Content-Disposition, where the server sent one. */
  filename: string | null;
  /**
   * `signed` when the bytes are the copy somebody actually put a certificate
   * to, `rendered` when they are a fresh render of the same permit. Only the
   * generated-document endpoint sends it; null everywhere else.
   */
  source: "signed" | "rendered" | null;
};

/**
 * Fetch a protected file into memory.
 *
 * Navigating with `window.location.href` cannot work for these URLs: the JWT
 * lives in localStorage, not a cookie, so the browser sends no Authorization
 * header and the API answers 401. Fetch the bytes with the token instead.
 *
 * The response is *not* asked to bypass the HTTP cache. The document endpoint
 * sends `ETag` + `must-revalidate`, so a second fetch of an unchanged permit is
 * a conditional request the server answers 304 to without re-rendering — worth
 * roughly 1.8s of docxtpl and LibreOffice on every reopened preview.
 */
export async function fetchFile(
  url: string,
  /**
   * Send the JWT. True for anything under `/api/`, false for an uploaded
   * attachment: those are served as plain static files, and an Authorization
   * header on a cross-origin GET turns a simple request into a preflighted one
   * for no gain — which the static handler answers 405 to.
   */
  authenticated = true,
): Promise<FetchedFile> {
  const token = authenticated ? await getValidAccessToken() : null;

  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    let detail = "";
    try {
      const body: { message?: string; detail?: string } = await response.json();
      detail = body?.message ?? body?.detail ?? "";
    } catch {
      /* not a JSON error body */
    }
    const error: Error & { status?: number } = new Error(
      detail || `Download failed (${response.status})`,
    );
    // The preview distinguishes 503 ("this server has no PDF converter — here
    // is the DOCX") from a real failure, and cannot do that from the text.
    error.status = response.status;
    throw error;
  }

  const source = response.headers.get("X-Document-Source");
  return {
    blob: await response.blob(),
    filename: filenameFrom(response),
    source: source === "signed" || source === "rendered" ? source : null,
  };
}

/** Save a blob to disk under `name`, via a temporary anchor. */
export function saveBlob(blob: Blob, name: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Give the browser a moment to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
  }
}

/** Download a protected file to disk. */
export async function downloadFile(
  url: string,
  fallbackName = "download",
): Promise<void> {
  const { blob, filename } = await fetchFile(url);
  saveBlob(blob, filename ?? fallbackName);
}

/** Read the server-supplied filename out of Content-Disposition, if present. */
function filenameFrom(response: Response): string | null {
  const header = response.headers.get("Content-Disposition");
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) return decodeURIComponent(utf8[1].trim());
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1].trim() : null;
}
